import type {
  ConversationTask,
  ConversationTaskStatus,
  MessageBlock,
  RunStatus,
  TaskPlan,
} from "@rakazo/contracts";
import { ConversationTaskSchema, TaskPlanSchema } from "@rakazo/contracts";

/** Execution owns liveness; an agent's plan can never override a failure or a pending approval. */
export function conversationTaskStatus(status: RunStatus, plan: TaskPlan): ConversationTaskStatus {
  if (status === "failed" || status === "cancelled") return status;
  if (status === "waiting_input" || status === "waiting_takeover") return "needs_input";
  if (status === "queued") return "queued";
  if (status === "running" || status === "leased") return "working";
  return plan.outcome === "working" ? "paused" : plan.outcome;
}

export function projectConversationTask(run: {
  id: string;
  taskId: string;
  status: string;
  sourceMessageId?: string | null;
  task: { action?: unknown };
}): ConversationTask | undefined {
  const parsed = TaskPlanSchema.safeParse(run.task?.action);
  if (!parsed.success) return undefined;
  const plan = parsed.data;
  return {
    taskId: run.taskId,
    runId: run.id,
    title: plan.title,
    steps: plan.steps,
    summary: plan.summary,
    outcome: plan.outcome,
    status: conversationTaskStatus(run.status as RunStatus, plan),
    ...(run.sourceMessageId ? { messageId: run.sourceMessageId } : {}),
  };
}

export const TASK_STATUS_ORDER: ConversationTaskStatus[] = [
  "needs_input",
  "review",
  "working",
  "queued",
  "paused",
  "failed",
  "done",
  "cancelled",
];

export const CONVERSATION_TASK_GUIDANCE = `Conversation tasks: use task_update before carrying out an actionable request, with a short human title and only meaningful steps. Do not create a task for greetings, explanations, or ordinary questions. This tracks work in Messages; it does not create a mission or start other agents. Reuse step ids and update the plan when the user corrects the request. Report only observed progress: keep unfinished steps pending or working; after verification use outcome done, or review when the user must inspect a result. Ending a reply is not completion. Use ask_user or request_takeover for input; the execution status will show Needs you automatically. Share useful images, files, charts, or computer handoffs through the existing tools when they help the user inspect a result. Do not manufacture live previews or unsolicited work.`;

/** Apply authoritative task updates and execution transitions to every loaded message, including older pages. */
export function updateConversationTaskMessages<
  T extends { role?: string; runId?: string; task?: ConversationTask },
>(messages: T[], event: { type: string; runId?: string; payload?: Record<string, unknown> }): T[] {
  if (event.type === "thread.task.updated") {
    const result = ConversationTaskSchema.safeParse(event.payload?.task);
    if (!result.success) return messages;
    return messages.map((message) =>
      message.role === "bot" && message.runId === result.data.runId
        ? { ...message, task: result.data }
        : message,
    );
  }
  const status: RunStatus | undefined = (
    {
      "run.started": "running",
      "run.waiting_input": "waiting_input",
      "computer.takeover.requested": "waiting_takeover",
      "run.completed": "completed",
      "run.failed": "failed",
      "run.cancelled": "cancelled",
    } as Record<string, RunStatus>
  )[event.type];
  if (!status || !event.runId) return messages;
  let changed = false;
  const next = messages.map((message) => {
    if (!message.task || message.runId !== event.runId) return message;
    const nextStatus = conversationTaskStatus(status, message.task);
    if (nextStatus === message.task.status) return message;
    changed = true;
    return { ...message, task: { ...message.task, status: nextStatus } };
  });
  return changed ? next : messages;
}

export function hideRedundantTaskMarkers<
  T extends { id: string; runId?: string; role: string; blocks: MessageBlock[] },
>(messages: readonly T[], revealId?: string): T[] {
  const answered = new Set(
    messages
      .filter(
        (message) =>
          message.role === "bot" &&
          message.blocks.some(
            (block) => !["task", "steps", "progress", "task_suggestion"].includes(block.kind),
          ),
      )
      .map((message) => message.runId),
  );
  return messages.filter(
    (message) =>
      message.id === revealId ||
      !message.runId ||
      !answered.has(message.runId) ||
      !message.blocks.every((block) => block.kind === "task"),
  );
}
